import React, { useState, useMemo, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Loader2, AlertCircle, GraduationCap, User, MapPin, BookOpen, Briefcase, Phone, FileText, ShieldCheck } from 'lucide-react';
import { COURSES } from '../data/mock';
import { useSiteLanguage } from '../context/SiteLanguageContext';
import Seo from '../components/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function normalizeCourseOption(course) {
  if (!course) {
    return null;
  }

  return {
    ...course,
    category: course.category || 'Other',
    price: Number(course.price) || 0,
    slug: course.slug || course.id || '',
    title: course.title || 'Untitled course',
  };
}

function formatLocalizedPrice(price, language) {
  const numericPrice = Number(price) || 0;
  const locale = language === 'ar' ? 'ar-EG' : 'en-GB';

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: numericPrice % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(numericPrice);
}

function interpolate(template, values) {
  return Object.entries(values).reduce(
    (result, [key, value]) => result.split(`{${key}}`).join(String(value ?? '')),
    template
  );
}

const COUNTRIES = [
  'United Kingdom', 'United States', 'Afghanistan', 'Albania', 'Algeria', 'Argentina', 'Australia', 'Austria',
  'Bangladesh', 'Belgium', 'Bhutan', 'Brazil', 'Bulgaria', 'Canada', 'China', 'Cyprus', 'Denmark', 'Egypt',
  'Estonia', 'Ethiopia', 'Finland', 'France', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Hong Kong', 'Hungary',
  'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy', 'Jamaica', 'Japan', 'Jordan',
  'Kazakhstan', 'Kenya', 'Kuwait', 'Latvia', 'Lebanon', 'Libya', 'Lithuania', 'Luxembourg', 'Malaysia', 'Malta',
  'Mexico', 'Morocco', 'Nepal', 'Netherlands', 'New Zealand', 'Nigeria', 'Norway', 'Oman', 'Pakistan', 'Philippines',
  'Poland', 'Portugal', 'Qatar', 'Romania', 'Russia', 'Saudi Arabia', 'Serbia', 'Singapore', 'Slovakia', 'Slovenia',
  'Somalia', 'South Africa', 'South Korea', 'Spain', 'Sri Lanka', 'Sudan', 'Sweden', 'Switzerland', 'Syria',
  'Taiwan', 'Tanzania', 'Thailand', 'Tunisia', 'Turkey', 'Uganda', 'Ukraine', 'United Arab Emirates', 'Uruguay',
  'Uzbekistan', 'Venezuela', 'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe',
];

const HEAR_OPTIONS = ['Google', 'Bing', 'Social Media', 'Friend / Family / Referral', 'Other (Please Specify)'];
const STUDY_MODE_OPTIONS = ['Online', 'Blended learning'];
const FEES_MODE_OPTIONS = ['Self Funded', 'Company Sponsorship', 'Bank Loan'];
const PAYMENT_METHOD_OPTIONS = ['Pay in full (one time payment)', 'Monthly installments'];
const TITLE_OPTIONS = ['Mr.', 'Mrs.', 'Miss.', 'Ms.', 'Dr.'];
const GENDER_OPTIONS = ['Male', 'Female', 'Others'];

const initialState = {
  // Course selection
  course_id: '', course_title: '',
  study_mode: 'Online',
  fees_mode: 'Self Funded',
  payment_method: 'Pay in full (one time payment)',
  starting_date: '',
  previously_studied: 'No',
  heard_about: '',
  heard_about_other: '',
  promo_code: '',
  // Personal info
  title: '',
  first_name: '', middle_name: '', surname: '',
  dob: '', gender: '', mobile: '', email: '',
  nationality: '', country_of_birth: '',
  // Home address
  address_line_1: '', address_line_2: '', city: '', state: '', postal_code: '', country: '',
  // Correspondence address
  correspondence_same: true,
  corr_address_line_1: '', corr_address_line_2: '', corr_city: '', corr_state: '', corr_postal_code: '', corr_country: '',
  // Previous education
  prev_course: '', prev_start_date: '', prev_end_date: '',
  // Work experience
  job_title: '', employer: '', work_start_date: '', work_end_date: '', responsibility: '',
  // Emergency / supply info
  supply_name: '', supply_relationship: '', supply_contact_number: '',
  // Document filenames (no upload — captured for reference)
  doc_id_name: '', doc_edu_name: '', doc_exp_name: '',
  // Consent
  consent: false,
};

export default function Apply() {
  const { language, t } = useSiteLanguage();
  const [params] = useSearchParams();
  const preselectSlug = params.get('course');
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [courses, setCourses] = useState(() => COURSES.map(normalizeCourseOption).filter(Boolean));
  const [coursesLoading, setCoursesLoading] = useState(true);

  const studyModeOptions = useMemo(() => STUDY_MODE_OPTIONS.map(option => ({
    value: option,
    label: option === 'Online'
      ? t('apply.studyMode.online', 'Online')
      : t('apply.studyMode.blended', 'Blended learning'),
  })), [t]);

  const feesModeOptions = useMemo(() => FEES_MODE_OPTIONS.map(option => ({
    value: option,
    label: option === 'Self Funded'
      ? t('apply.feesMode.selfFunded', 'Self Funded')
      : option === 'Company Sponsorship'
        ? t('apply.feesMode.company', 'Company Sponsorship')
        : t('apply.feesMode.loan', 'Bank Loan'),
  })), [t]);

  const paymentMethodOptions = useMemo(() => PAYMENT_METHOD_OPTIONS.map(option => ({
    value: option,
    label: option === 'Pay in full (one time payment)'
      ? t('apply.paymentMethod.full', 'Pay in full (one time payment)')
      : t('apply.paymentMethod.installments', 'Monthly installments'),
  })), [t]);

  const heardAboutOptions = useMemo(() => HEAR_OPTIONS.map(option => ({
    value: option,
    label: option === 'Google'
      ? t('apply.hear.google', 'Google')
      : option === 'Bing'
        ? t('apply.hear.bing', 'Bing')
        : option === 'Social Media'
          ? t('apply.hear.social', 'Social Media')
          : option === 'Friend / Family / Referral'
            ? t('apply.hear.referral', 'Friend / Family / Referral')
            : t('apply.hear.other', 'Other (Please Specify)'),
  })), [t]);

  const titleOptions = useMemo(() => TITLE_OPTIONS.map(option => ({
    value: option,
    label: option === 'Mr.'
      ? t('apply.title.mr', 'Mr.')
      : option === 'Mrs.'
        ? t('apply.title.mrs', 'Mrs.')
        : option === 'Miss.'
          ? t('apply.title.miss', 'Miss.')
          : option === 'Ms.'
            ? t('apply.title.ms', 'Ms.')
            : t('apply.title.dr', 'Dr.'),
  })), [t]);

  const genderOptions = useMemo(() => GENDER_OPTIONS.map(option => ({
    value: option,
    label: option === 'Male'
      ? t('apply.gender.male', 'Male')
      : option === 'Female'
        ? t('apply.gender.female', 'Female')
        : t('apply.gender.other', 'Others'),
  })), [t]);

  const grouped = useMemo(() => {
    const map = {};
    courses.forEach(c => {
      if (!map[c.category]) map[c.category] = [];
      map[c.category].push(c);
    });
    return map;
  }, [courses]);

  const selectedCourse = useMemo(
    () => courses.find(course => course.slug === form.course_id) || null,
    [courses, form.course_id]
  );

  useEffect(() => {
    let ignore = false;

    axios.get(`${API}/courses`)
      .then(res => {
        if (ignore) return;
        const nextCourses = Array.isArray(res.data)
          ? res.data.map(normalizeCourseOption).filter(Boolean)
          : [];
        if (nextCourses.length > 0) {
          setCourses(nextCourses);
        }
      })
      .catch(() => {
        if (!ignore) {
          setCourses(COURSES.map(normalizeCourseOption).filter(Boolean));
        }
      })
      .finally(() => {
        if (!ignore) {
          setCoursesLoading(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, []);

  // Pre-select course from URL
  useEffect(() => {
    if (!preselectSlug || courses.length === 0) {
      return;
    }

    const found = courses.find(c => c.slug === preselectSlug);
    if (found) {
      setForm(f => {
        if (f.course_id === found.slug && f.course_title === found.title) {
          return f;
        }
        return { ...f, course_id: found.slug, course_title: found.title };
      });
    }
  }, [courses, preselectSlug]);

  const set = (name) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(f => ({ ...f, [name]: value }));
  };

  const onCourseChange = (e) => {
    const slug = e.target.value;
    const c = courses.find(x => x.slug === slug);
    setForm(f => ({ ...f, course_id: slug, course_title: c ? c.title : '' }));
  };

  const onFile = (name) => (e) => {
    const file = e.target.files?.[0];
    setForm(f => ({ ...f, [name]: file ? file.name : '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.consent) {
      setError(t('apply.errorConsent', 'Please accept the disclaimer to continue.'));
      return;
    }
    setSubmitting(true);
    try {
      const finalCorrespondence = form.correspondence_same
        ? {
            corr_address_line_1: form.address_line_1,
            corr_address_line_2: form.address_line_2,
            corr_city: form.city,
            corr_state: form.state,
            corr_postal_code: form.postal_code,
            corr_country: form.country,
          }
        : {
            corr_address_line_1: form.corr_address_line_1,
            corr_address_line_2: form.corr_address_line_2,
            corr_city: form.corr_city,
            corr_state: form.corr_state,
            corr_postal_code: form.corr_postal_code,
            corr_country: form.corr_country,
          };

      const enrolmentData = { ...form, ...finalCorrespondence };

      const response = await axios.post(`${API}/payments/enrolment-checkout`, {
        course_slug: form.course_id,
        customer_email: form.email || undefined,
        success_url: `${window.location.origin}/apply/success`,
        cancel_url: `${window.location.origin}/apply?course=${form.course_id}`,
        enrolment: enrolmentData,
      });

      if (response.data?.checkout_url) {
        // Stripe is configured — redirect to hosted checkout
        window.location.assign(response.data.checkout_url);
        return;
      }

      // Stripe not yet configured — enrolment was saved; show success screen
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err?.response?.data?.detail || t('apply.errorSubmission', 'Submission failed. Please check your details and try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 py-16 px-4">
        <div className="max-w-xl mx-auto bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center" data-testid="apply-success">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <CheckCircle size={32} className="text-green-600" />
          </div>
          <h1 className="text-2xl font-extrabold text-gray-900 mb-2">{t('apply.applicationReceived', 'Application received')}</h1>
          <p className="text-gray-500 text-sm leading-relaxed mb-2">
            {interpolate(t('apply.successIntro', 'Thank you, {firstName}. Your application for {courseTitle} has been submitted successfully.'), {
              firstName: form.first_name,
              courseTitle: form.course_title,
            })}
          </p>
          <p className="text-gray-500 text-sm leading-relaxed mb-8">
            {interpolate(t('apply.successFollowup', 'A member of our admissions team will contact you within one working day to verify your details and complete your enrolment. A confirmation email has been sent to {email}.'), {
              email: form.email,
            })}
          </p>
          <div className="flex gap-3 justify-center">
            <Link to="/courses" className="px-5 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
              {t('apply.browseMoreCourses', 'Browse more courses')}
            </Link>
            <Link to="/" className="px-5 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors">
              {t('apply.returnHome', 'Return to home')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Seo
        title="Apply Now"
        description="Apply online for an accredited Atlas Academy course in minutes. Flexible start dates and payment plans available."
        path="/apply"
      />
      {/* Hero */}
      <div className="bg-[#0f2b4a] text-white py-12">
        <div className="max-w-4xl mx-auto px-4">
          <p className="text-blue-300 text-sm font-medium mb-2">{t('apply.heroEyebrow', 'Online application form')}</p>
          <h1 className="text-3xl lg:text-4xl font-extrabold mb-3">{t('apply.heroTitle', 'Apply for Your Course')}</h1>
          <p className="text-blue-100/70 text-sm leading-relaxed max-w-2xl">
            {t('apply.heroDescription', 'Information requested on this form should be completed in as much detail in order to process your application successfully. All fields marked * must be completed.')}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="max-w-4xl mx-auto px-4 py-10 space-y-6" data-testid="apply-form">
        {/* Section 1: Course selection */}
        <Section icon={<GraduationCap size={18} />} title={t('apply.courseSelection', 'Course Selection')}>
          <Field label={t('apply.selectCourse', 'Select course')} required>
            <select
              required
              value={form.course_id}
              onChange={onCourseChange}
              data-testid="apply-course-select"
              className="form-input"
            >
              <option value="">{coursesLoading ? t('apply.loadingCourses', '-- Loading courses --') : t('apply.selectCoursePlaceholder', '-- Select a course --')}</option>
              {Object.keys(grouped).map(cat => (
                <optgroup key={cat} label={cat}>
                  {grouped[cat].map(c => (
                    <option key={c.slug} value={c.slug}>{`${c.title} — £${formatLocalizedPrice(c.price, language)}`}</option>
                  ))}
                </optgroup>
              ))}
            </select>
            {selectedCourse && (
              <p className="mt-2 text-xs text-gray-500">
                <span className="font-semibold text-gray-700">{interpolate(t('apply.currentCourseFee', 'Current course fee: £{amount}'), { amount: formatLocalizedPrice(selectedCourse.price, language) })}</span>
              </p>
            )}
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label={t('apply.studyMode', 'Study Mode')}>
              <RadioGroup
                name="study_mode"
                value={form.study_mode}
                onChange={set('study_mode')}
                options={studyModeOptions}
                testidPrefix="study-mode"
              />
            </Field>
            <Field label={t('apply.feesMode', 'Course Fees (Mode)')}>
              <RadioGroup
                name="fees_mode"
                value={form.fees_mode}
                onChange={set('fees_mode')}
                options={feesModeOptions}
                testidPrefix="fees-mode"
              />
            </Field>
            <Field label={t('apply.paymentMethod', 'Payment Method')}>
              <RadioGroup
                name="payment_method"
                value={form.payment_method}
                onChange={set('payment_method')}
                options={paymentMethodOptions}
                testidPrefix="payment-method"
              />
            </Field>
            <Field label={t('apply.courseStartingDate', 'Course Starting Date')}>
              <input type="date" value={form.starting_date} onChange={set('starting_date')} className="form-input" data-testid="apply-start-date" />
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label={t('apply.previouslyStudied', 'Have you previously studied at Atlas Academy?')}>
              <RadioGroup
                name="previously_studied"
                value={form.previously_studied}
                onChange={set('previously_studied')}
                options={[{ value: 'Yes', label: t('apply.yes', 'Yes') }, { value: 'No', label: t('apply.no', 'No') }]}
                testidPrefix="prev-studied"
              />
            </Field>
            <Field label={t('apply.heardAbout', 'Where did you first hear about us?')}>
              <select value={form.heard_about} onChange={set('heard_about')} className="form-input" data-testid="apply-heard-about">
                <option value="">{t('apply.selectOption', 'Select Option')}</option>
                {heardAboutOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </Field>
          </div>

          {form.heard_about === 'Other (Please Specify)' && (
            <Field label={t('apply.other', 'Other')}>
              <input value={form.heard_about_other} onChange={set('heard_about_other')} className="form-input" placeholder={t('apply.pleaseSpecify', 'Please specify')} data-testid="apply-heard-other" />
            </Field>
          )}

          <Field label={t('apply.promoCode', 'Promo Code / Referral Code (optional)')}>
            <input value={form.promo_code} onChange={set('promo_code')} className="form-input" data-testid="apply-promo-code" />
          </Field>
        </Section>

        {/* Section 2: Personal Information */}
        <Section icon={<User size={18} />} title={t('apply.personalInformation', 'Personal Information')}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <Field label={t('apply.title', 'Title')} required>
              <select required value={form.title} onChange={set('title')} className="form-input" data-testid="apply-title">
                <option value="">{t('apply.selectOption', 'Select Option')}</option>
                {titleOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </Field>
            <Field label={t('apply.firstName', 'First Name')} required>
              <input required value={form.first_name} onChange={set('first_name')} className="form-input" data-testid="apply-first-name" />
            </Field>
            <Field label={t('apply.middleName', 'Middle Name')}>
              <input value={form.middle_name} onChange={set('middle_name')} className="form-input" data-testid="apply-middle-name" />
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <Field label={t('apply.surname', 'Surname')} required>
              <input required value={form.surname} onChange={set('surname')} className="form-input" data-testid="apply-surname" />
            </Field>
            <Field label={t('apply.dateOfBirth', 'Date of Birth')} required>
              <input type="date" required value={form.dob} onChange={set('dob')} className="form-input" data-testid="apply-dob" />
            </Field>
            <Field label={t('apply.gender', 'Gender')} required>
              <select required value={form.gender} onChange={set('gender')} className="form-input" data-testid="apply-gender">
                <option value="">{t('apply.selectOption', 'Select Option')}</option>
                {genderOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label={t('apply.mobileNumber', 'Mobile Number')} required>
              <input required type="tel" dir="ltr" value={form.mobile} onChange={set('mobile')} className="form-input" placeholder="+44 7700 900000" data-testid="apply-mobile" />
            </Field>
            <Field label={t('apply.email', 'Email')} required>
              <input required type="email" value={form.email} onChange={set('email')} className="form-input" data-testid="apply-email" />
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label={t('apply.nationality', 'Nationality')} required>
              <input required value={form.nationality} onChange={set('nationality')} className="form-input" data-testid="apply-nationality" />
            </Field>
            <Field label={t('apply.countryOfBirth', 'Country of Birth')}>
              <CountrySelect value={form.country_of_birth} onChange={set('country_of_birth')} testid="apply-country-birth" />
            </Field>
          </div>
        </Section>

        {/* Section 3: Home Address */}
        <Section icon={<MapPin size={18} />} title={t('apply.homeAddress', 'Home Address')}>
          <Field label={t('apply.addressLine1', 'Address Line 1')}>
            <input value={form.address_line_1} onChange={set('address_line_1')} className="form-input" data-testid="apply-addr1" />
          </Field>
          <Field label={t('apply.addressLine2', 'Address Line 2')}>
            <input value={form.address_line_2} onChange={set('address_line_2')} className="form-input" data-testid="apply-addr2" />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label={t('apply.city', 'City')}><input value={form.city} onChange={set('city')} className="form-input" data-testid="apply-city" /></Field>
            <Field label={t('apply.stateRegion', 'State / Province / Region')}><input value={form.state} onChange={set('state')} className="form-input" data-testid="apply-state" /></Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label={t('apply.postalCode', 'Postal Code')}><input value={form.postal_code} onChange={set('postal_code')} className="form-input" data-testid="apply-postal" /></Field>
            <Field label={t('apply.country', 'Country')}><CountrySelect value={form.country} onChange={set('country')} testid="apply-country" /></Field>
          </div>
        </Section>

        {/* Section 4: Correspondence Address */}
        <Section icon={<MapPin size={18} />} title={t('apply.correspondenceAddress', 'Correspondence Address')}>
          <label className="flex items-center gap-2 text-sm text-gray-700 mb-2">
            <input type="checkbox" checked={form.correspondence_same} onChange={set('correspondence_same')} data-testid="apply-corr-same" />
            {t('apply.sameAsHome', 'Same as home address')}
          </label>
          {!form.correspondence_same && (
            <div className="space-y-4 pt-2">
              <Field label={t('apply.addressLine1', 'Address Line 1')}>
                <input value={form.corr_address_line_1} onChange={set('corr_address_line_1')} className="form-input" />
              </Field>
              <Field label={t('apply.addressLine2', 'Address Line 2')}>
                <input value={form.corr_address_line_2} onChange={set('corr_address_line_2')} className="form-input" />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label={t('apply.city', 'City')}><input value={form.corr_city} onChange={set('corr_city')} className="form-input" /></Field>
                <Field label={t('apply.stateRegion', 'State / Province / Region')}><input value={form.corr_state} onChange={set('corr_state')} className="form-input" /></Field>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label={t('apply.postalCode', 'Postal Code')}><input value={form.corr_postal_code} onChange={set('corr_postal_code')} className="form-input" /></Field>
                <Field label={t('apply.country', 'Country')}><CountrySelect value={form.corr_country} onChange={set('corr_country')} testid="apply-corr-country" /></Field>
              </div>
            </div>
          )}
        </Section>

        {/* Section 5: Previous Education */}
        <Section icon={<BookOpen size={18} />} title={t('apply.previousEducation', 'Details of Previous Education')}>
          <Field label={t('apply.qualification', 'Course / Qualification')}>
            <input value={form.prev_course} onChange={set('prev_course')} className="form-input" data-testid="apply-prev-course" />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label={t('apply.startDate', 'Start Date')}><input type="date" value={form.prev_start_date} onChange={set('prev_start_date')} className="form-input" /></Field>
            <Field label={t('apply.endDate', 'End Date')}><input type="date" value={form.prev_end_date} onChange={set('prev_end_date')} className="form-input" /></Field>
          </div>
        </Section>

        {/* Section 6: Work Experience */}
        <Section icon={<Briefcase size={18} />} title={t('apply.workExperience', 'Work Experience (Optional)')}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label={t('apply.jobTitle', 'Job Title')}><input value={form.job_title} onChange={set('job_title')} className="form-input" data-testid="apply-job-title" /></Field>
            <Field label={t('apply.employer', 'Employer')}><input value={form.employer} onChange={set('employer')} className="form-input" data-testid="apply-employer" /></Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label={t('apply.startDate', 'Start Date')}><input type="date" value={form.work_start_date} onChange={set('work_start_date')} className="form-input" /></Field>
            <Field label={t('apply.endDate', 'End Date')}><input type="date" value={form.work_end_date} onChange={set('work_end_date')} className="form-input" /></Field>
          </div>
          <Field label={t('apply.responsibility', 'Responsibility')}>
            <textarea rows={3} value={form.responsibility} onChange={set('responsibility')} className="form-input resize-none" />
          </Field>
        </Section>

        {/* Section 7: Supply info / Emergency contact */}
        <Section icon={<Phone size={18} />} title={t('apply.supplyInfo', 'Supply Information for Application')}>
          <Field label={t('apply.fullName', 'Full Name')}>
            <input value={form.supply_name} onChange={set('supply_name')} className="form-input" data-testid="apply-supply-name" />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label={t('apply.relationshipToYou', 'Relationship to you')}><input value={form.supply_relationship} onChange={set('supply_relationship')} className="form-input" data-testid="apply-supply-rel" /></Field>
            <Field label={t('apply.contactNumber', 'Contact Number')}><input type="tel" dir="ltr" value={form.supply_contact_number} onChange={set('supply_contact_number')} className="form-input" data-testid="apply-supply-number" /></Field>
          </div>
        </Section>

        {/* Section 8: Documents */}
        <Section icon={<FileText size={18} />} title={t('apply.documents', 'Important Documents')}>
          <p className="text-xs text-gray-500 mb-3">
            {t('apply.documentsHelpPrefix', 'Attach identification and supporting documents. If you cannot attach here, you can email them to')}
            <a href="mailto:admissions@atlas-academy.co.uk" className="text-blue-600 ml-1">admissions@atlas-academy.co.uk</a>
            {' '}{t('apply.documentsHelpSuffix', 'after submission.')}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <DocField label={t('apply.idDocument', 'ID (Passport / Driving Licence)')} name="doc_id_name" filename={form.doc_id_name} onChange={onFile('doc_id_name')} testid="apply-doc-id" />
            <DocField label={t('apply.educationDocuments', 'Education Documents')} name="doc_edu_name" filename={form.doc_edu_name} onChange={onFile('doc_edu_name')} testid="apply-doc-edu" />
            <DocField label={t('apply.experienceDocuments', 'Experience Documents')} name="doc_exp_name" filename={form.doc_exp_name} onChange={onFile('doc_exp_name')} testid="apply-doc-exp" />
          </div>
        </Section>

        {/* Disclaimer */}
        <Section icon={<ShieldCheck size={18} />} title={t('apply.disclaimer', 'Disclaimer')}>
          <p className="text-xs text-gray-600 leading-relaxed mb-4">
            {t('apply.disclaimerText', 'This is a secure page and your data is encrypted so that it cannot be easily read by anyone who might intercept it between your computer and this website. The data is made available to academic and administrative departments within the Atlas Academy and is used for processing applications only. All data will be treated confidentially. We are committed to protecting your rights and privacy. Please note that the Atlas Academy is under no obligation to respond to any Application Forms which are not completed in full. Check your information carefully before submitting this form.')}
          </p>
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input required type="checkbox" checked={form.consent} onChange={set('consent')} className="mt-1" data-testid="apply-consent" />
            <span>{t('apply.consentText', 'I confirm that the information provided is true and accurate, and I consent to the processing of my personal data for the purposes of this application.')} <span className="text-red-500">*</span></span>
          </label>
        </Section>

        {error && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm" data-testid="apply-error">
            <AlertCircle size={15} /> {error}
          </div>
        )}

        <div className="flex items-center justify-end pt-2">
          <button
            type="submit"
            disabled={submitting}
            data-testid="apply-submit"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-10 py-3.5 rounded-lg font-semibold text-sm transition-colors"
          >
            {submitting ? <><Loader2 size={16} className="animate-spin" /> {t('apply.submitting', 'Submitting…')}</> : t('apply.submit', 'Submit Application')}
          </button>
        </div>
      </form>

      <style>{`
        .form-input {
          width: 100%;
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          padding: 0.6rem 0.85rem;
          font-size: 0.875rem;
          background: #fff;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .form-input:focus {
          outline: none;
          border-color: #60a5fa;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
        }
      `}</style>
    </div>
  );
}

// ── Helper components ──────────────────────────────────────────────────────
function Section({ icon, title, children }) {
  return (
    <section className="bg-white border border-gray-100 rounded-xl shadow-sm p-6 lg:p-7">
      <div className="flex items-center gap-2 pb-4 border-b border-gray-100 mb-5">
        <div className="w-8 h-8 bg-blue-50 text-blue-700 rounded-lg flex items-center justify-center">{icon}</div>
        <h2 className="text-base font-bold text-gray-900">{title}</h2>
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
    </div>
  );
}

function RadioGroup({ name, value, onChange, options, testidPrefix }) {
  const normalizedOptions = options.map(option => (typeof option === 'string' ? { value: option, label: option } : option));

  return (
    <div className="flex flex-wrap gap-3 pt-1">
      {normalizedOptions.map(opt => (
        <label
          key={opt.value}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors ${
            value === opt.value ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-700 hover:border-gray-300'
          }`}
        >
          <input
            type="radio"
            name={name}
            value={opt.value}
            checked={value === opt.value}
            onChange={onChange}
            data-testid={`${testidPrefix}-${opt.value.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            className="accent-blue-600"
          />
          {opt.label}
        </label>
      ))}
    </div>
  );
}

function CountrySelect({ value, onChange, testid }) {
  const { t } = useSiteLanguage();

  return (
    <select value={value} onChange={onChange} className="form-input" data-testid={testid}>
      <option value="">{t('apply.countryPlaceholder', '-- Select country --')}</option>
      {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
    </select>
  );
}

function DocField({ label, name, filename, onChange, testid }) {
  const { t } = useSiteLanguage();

  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1.5">{label}</label>
      <label className="cursor-pointer block">
        <div className={`border border-dashed rounded-lg px-3 py-3 text-center text-xs transition-colors ${
          filename ? 'border-blue-400 bg-blue-50 text-blue-700' : 'border-gray-300 text-gray-500 hover:border-blue-400 hover:bg-blue-50/50'
        }`}>
          {filename ? (
            <span className="font-medium truncate block">{filename}</span>
          ) : (
            <>{t('apply.uploadPrompt', 'Click to upload')} <br /><span className="text-[10px] text-gray-400">{t('apply.uploadFormats', 'PDF, JPG or PNG')}</span></>
          )}
        </div>
        <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={onChange} className="hidden" data-testid={testid} />
      </label>
    </div>
  );
}
